import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BidForm, { getDefaultBidFormData, type BidFormData } from "./components/BidForm";
import BidResult, { calculateBid } from "./components/BidResult";
import ReverseForm, { getDefaultReverseFormData, type ReverseFormData } from "./components/ReverseForm";
import ReverseResult, { calculateReverse } from "./components/ReverseResult";
import { auctionItems } from "@/mocks/auctionItems";
import { auctionToNormalBidData, auctionToReverseBidData } from "@/utils/auctionToBidData";

type TabMode = "normal" | "reverse";

export default function BidCalculatorPage() {
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get("itemId");
  const initialMode = (searchParams.get("mode") as TabMode | null) ?? "normal";

  const [activeTab, setActiveTab] = useState<TabMode>(initialMode);
  const [prefilledItem, setPrefilledItem] = useState<(typeof auctionItems)[number] | null>(null);
  const [showPrefilledBanner, setShowPrefilledBanner] = useState(false);

  // 일반방식 상태 — itemId 있으면 경매 물건 데이터로 초기화
  const [normalData, setNormalData] = useState<BidFormData>(() => {
    if (itemId) {
      const found = auctionItems.find((a) => a.id === itemId);
      if (found) return auctionToNormalBidData(found);
    }
    return getDefaultBidFormData();
  });

  // 시세역산방식 상태 — itemId 있으면 경매 물건 데이터로 초기화
  const [reverseData, setReverseData] = useState<ReverseFormData>(() => {
    if (itemId) {
      const found = auctionItems.find((a) => a.id === itemId);
      if (found) return auctionToReverseBidData(found);
    }
    return getDefaultReverseFormData();
  });

  // pre-fill 배너 표시
  useEffect(() => {
    if (itemId) {
      const found = auctionItems.find((a) => a.id === itemId);
      if (found) {
        setPrefilledItem(found);
        setShowPrefilledBanner(true);
      }
    }
  }, [itemId]);

  const normalResults = useMemo(() => calculateBid(normalData), [normalData]);
  const reverseResults = useMemo(() => calculateReverse(reverseData), [reverseData]);

  const handleReverseMarginChange = (v: number) => {
    setReverseData((prev) => ({
      ...prev,
      targetMargin: v,
      targetROI: prev.acuteMarketPrice > 0 ? Math.round((v / prev.acuteMarketPrice) * 100) : 0,
    }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">

          {/* Pre-filled 알림 배너 */}
          <AnimatePresence>
            {showPrefilledBanner && prefilledItem && (
              <motion.div
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }}
                className="mb-5 bg-gradient-to-r from-[#1C1917] to-[#3A2E28] rounded-xl px-5 py-3.5 flex items-center gap-4"
              >
                <div className="w-8 h-8 rounded-lg bg-[#C4956A]/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-magic-line text-[#C4956A]"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white leading-snug">
                    물건 데이터 자동 입력 완료
                  </p>
                  <p className="text-xs text-[#B8B3AE] mt-0.5 truncate">
                    <span className="text-[#C4956A] font-medium">{prefilledItem.caseNumber}</span>
                    {" "}({prefilledItem.location}) — 두 방식 모두 이 물건 기준으로 세팅되었습니다
                  </p>
                </div>
                <Link
                  to={`/auctions/${prefilledItem.id}`}
                  className="text-xs text-[#C4956A] hover:text-white transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  물건 보기
                  <i className="ri-arrow-right-line text-[10px]"></i>
                </Link>
                <button
                  onClick={() => setShowPrefilledBanner(false)}
                  className="w-6 h-6 rounded-full flex items-center justify-center text-[#8C8780] hover:text-white transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-calculator-line text-[#C4956A]"></i>
                <span className="text-xs font-medium text-[#C4956A]">도구</span>
              </div>
              <Link
                to="/bid-calculator-guide"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#8C8780] border border-[#E8E5E0] rounded-lg hover:text-[#C4956A] hover:border-[#C4956A] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-text-line"></i>
                계산법 설명서
              </Link>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-1">
              경매 입찰가 계산기
            </h1>
            <p className="text-sm text-[#8C8780]">
              두 가지 방식으로 적정 입찰가를 산출합니다
            </p>
          </motion.div>

          {/* Tab Switcher */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.4 }}
            className="mb-8"
          >
            <div className="inline-flex p-1 bg-[#F0EDE8] rounded-xl gap-1">
              <button
                onClick={() => setActiveTab("normal")}
                className={`relative px-5 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "normal"
                    ? "bg-white text-[#1C1917] shadow-sm"
                    : "text-[#8C8780] hover:text-[#5C5650]"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <i className="ri-calculator-fill text-xs"></i>
                  일반방식
                </span>
              </button>
              <button
                onClick={() => setActiveTab("reverse")}
                className={`relative px-5 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === "reverse"
                    ? "bg-white text-[#1C1917] shadow-sm"
                    : "text-[#8C8780] hover:text-[#5C5650]"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <i className="ri-flashlight-fill text-xs text-[#C4956A]"></i>
                  시세역산방식
                  <span className="px-1.5 py-0.5 bg-[#C4956A]/10 text-[#C4956A] text-[10px] rounded font-semibold">NEW</span>
                </span>
              </button>
            </div>

            <AnimatePresence mode="wait">
              {activeTab === "normal" ? (
                <motion.p
                  key="normal-desc"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.2 }}
                  className="mt-2 text-xs text-[#8C8780]"
                >
                  매각 시세에서 인수 권리금 · 부대비용 · 목표 수익을 차감하여 적정 입찰가 3단계를 산출합니다
                </motion.p>
              ) : (
                <motion.p
                  key="reverse-desc"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.2 }}
                  className="mt-2 text-xs text-[#8C8780]"
                >
                  <span className="text-[#C4956A] font-medium">감정가 착시를 제거</span>하고, 실제 급매 시세에서 역산하여 손실 없는 투찰 상한가를 도출합니다 — P = V - M - C - R
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Main Layout */}
          <AnimatePresence mode="wait">
            {activeTab === "normal" ? (
              <motion.div
                key="normal-content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
                className="flex flex-col lg:flex-row gap-8"
              >
                <div className="lg:w-[55%]">
                  <BidForm data={normalData} onChange={setNormalData} />
                </div>
                <div className="lg:w-[45%]">
                  <div className="lg:sticky lg:top-24">
                    <BidResult results={normalResults} data={normalData} />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="reverse-content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
                className="flex flex-col lg:flex-row gap-8"
              >
                <div className="lg:w-[55%]">
                  <ReverseForm data={reverseData} onChange={setReverseData} />
                </div>
                <div className="lg:w-[45%]">
                  <div className="lg:sticky lg:top-24">
                    <ReverseResult
                      results={reverseResults}
                      data={reverseData}
                      onTargetMarginChange={handleReverseMarginChange}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 방식 비교 카드 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-10 bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
          >
            <h3 className="text-sm font-bold text-[#1C1917] mb-5 flex items-center gap-2">
              <i className="ri-scales-3-line text-[#C4956A]"></i>
              두 방식의 차이점
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                onClick={() => setActiveTab("normal")}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  activeTab === "normal"
                    ? "border-[#1C1917] bg-[#1C1917]/3"
                    : "border-[#E8E5E0] hover:border-[#C4956A]/50"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-calculator-fill text-[#1C1917]"></i>
                  <span className="text-sm font-bold text-[#1C1917]">일반방식</span>
                  {activeTab === "normal" && (
                    <span className="ml-auto px-1.5 py-0.5 bg-[#1C1917] text-white text-[10px] rounded font-medium">현재</span>
                  )}
                </div>
                <ul className="text-xs text-[#5C5650] space-y-1">
                  <li className="flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[#C4956A] text-[6px] mt-1.5 flex-shrink-0"></i>
                    최저가/감정가 기준으로 출발
                  </li>
                  <li className="flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[#C4956A] text-[6px] mt-1.5 flex-shrink-0"></i>
                    지역 낙찰가율 반영 3단계 추천
                  </li>
                  <li className="flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[#C4956A] text-[6px] mt-1.5 flex-shrink-0"></i>
                    안전가 · 추천가 · 공격가 구분
                  </li>
                </ul>
              </div>
              <div
                onClick={() => setActiveTab("reverse")}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  activeTab === "reverse"
                    ? "border-[#C4956A] bg-[#C4956A]/5"
                    : "border-[#E8E5E0] hover:border-[#C4956A]/50"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-flashlight-fill text-[#C4956A]"></i>
                  <span className="text-sm font-bold text-[#1C1917]">시세역산방식</span>
                  {activeTab === "reverse" && (
                    <span className="ml-auto px-1.5 py-0.5 bg-[#C4956A] text-white text-[10px] rounded font-medium">현재</span>
                  )}
                </div>
                <ul className="text-xs text-[#5C5650] space-y-1">
                  <li className="flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[#C4956A] text-[6px] mt-1.5 flex-shrink-0"></i>
                    <strong className="text-[#1C1917]">급매 시세</strong>에서 역산 출발 (감정가 착시 제거)
                  </li>
                  <li className="flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[#C4956A] text-[6px] mt-1.5 flex-shrink-0"></i>
                    마지노선 경고 + 투찰 불가 알림
                  </li>
                  <li className="flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[#C4956A] text-[6px] mt-1.5 flex-shrink-0"></i>
                    시세 ±10% 민감도 분석 매트릭스
                  </li>
                  <li className="flex items-start gap-1.5">
                    <i className="ri-checkbox-blank-circle-fill text-[#C4956A] text-[6px] mt-1.5 flex-shrink-0"></i>
                    실시간 슬라이더로 목표수익 역산
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* 하단 관련 링크 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-6 bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
          >
            <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
              <i className="ri-links-line text-[#C4956A]"></i>
              입찰 전 꼭 확인하세요
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/auctions"
                className="flex items-center gap-3 p-4 bg-[#FAFAF8] rounded-lg hover:bg-[#C4956A]/5 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A]/20 transition-colors">
                  <i className="ri-auction-line text-[#C4956A]"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1C1917]">경매 물건 찾기</p>
                  <p className="text-xs text-[#8C8780]">투자 가치 높은 물건 탐색</p>
                </div>
                <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
              </Link>
              <Link
                to="/reports"
                className="flex items-center gap-3 p-4 bg-[#FAFAF8] rounded-lg hover:bg-[#C4956A]/5 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A]/20 transition-colors">
                  <i className="ri-file-search-line text-[#C4956A]"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1C1917]">물건분석 리포트</p>
                  <p className="text-xs text-[#8C8780]">전문가 권리분석 확인</p>
                </div>
                <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
              </Link>
              <Link
                to="/investment-simulator"
                className="flex items-center gap-3 p-4 bg-gradient-to-br from-[#1C1917]/5 to-[#C4956A]/5 rounded-lg hover:from-[#1C1917]/10 hover:to-[#C4956A]/10 transition-all group border border-[#C4956A]/15"
              >
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/15 flex items-center justify-center group-hover:bg-[#C4956A]/25 transition-colors">
                  <i className="ri-brain-line text-[#C4956A]"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1C1917]">AI 투자 시뮬레이터</p>
                  <p className="text-xs text-[#8C8780]">순이익·ROE·투자등급 분석</p>
                </div>
                <i className="ri-arrow-right-line text-[#C4956A] ml-auto group-hover:translate-x-0.5 transition-all"></i>
              </Link>
              <Link
                to="/consultation"
                className="flex items-center gap-3 p-4 bg-[#FAFAF8] rounded-lg hover:bg-[#C4956A]/5 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center group-hover:bg-[#C4956A]/20 transition-colors">
                  <i className="ri-customer-service-2-line text-[#C4956A]"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1C1917]">1:1 상담 신청</p>
                  <p className="text-xs text-[#8C8780]">전문가와 전략 수립</p>
                </div>
                <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
              </Link>
            </div>
          </motion.div>

        </div>
      </main>
      <Footer />
    </div>
  );
}